/* ============================================================================
 * canonical v2 promotions-feed interpretation boundary — v2-ONLY (no v1 translation);
 * all feed consumers (shoppable-promotions engine, homepage promotional-slider)
 * MUST read the feed through this module; keep interpretation here, presentation
 * in the consumers.
 * ==========================================================================*/

window.IBPromoFeed = (function () {
  'use strict';

  function stripDollars(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/\$\s?\d[\d.,]*/g, '')
      .replace(/\s{2,}/g, ' ')
      .replace(/\s+([,.;:!?])/g, '$1')
      .replace(/\s*(?:--|[-–—]|,)\s*([.;:!?])/g, '$1')
      .replace(/\s*(?:--|[-–—]|,)\s*$/g, '')
      .trim();
  }

  // resolveSource(promo) — reads the explicit v2 promo.source.{type,handle}.
  // The feed is v2-ONLY (spec §1.3): there is NO websiteLink parsing. A promo
  // with no usable collection/product source becomes a 'link' source pointing at
  // source.fallbackLink, so it is never a dead end.
  function resolveSource(promo) {
    var src = (promo && promo.source) || {};
    var fallback = src.fallbackLink || '';
    if (src.handle && (src.type === 'collection' || src.type === 'product')) {
      return { type: src.type, handle: String(src.handle), fallbackLink: fallback };
    }
    return { type: 'link', kind: 'link', handle: null, fallbackLink: fallback || '#' };
  }
  function sourceHref(source) {
    var src = source || {};
    if (src.type === 'collection' && src.handle) return '/collections/' + src.handle;
    if (src.type === 'product' && src.handle) return '/products/' + src.handle;
    return src.fallbackLink || '';
  }

  // Build a human date label ("June 1 – August 31, 2026") from ISO start/end —
  // used ONLY when a v2 promo omits dates.display.
  var MONTHS = ['January','February','March','April','May','June','July',
    'August','September','October','November','December'];
  function ymdLabel(iso) {
    if (typeof iso !== 'string') return '';
    var m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
    var mi = parseInt(m[2], 10) - 1;
    return (mi >= 0 && mi < 12) ? MONTHS[mi] + ' ' + parseInt(m[3], 10) : '';
  }
  function rangeLabel(start, end) {
    var a = ymdLabel(start), b = ymdLabel(end);
    var year = String(end || start || '').slice(0, 4);
    if (a && b) return a + ' – ' + b + (year ? ', ' + year : '');
    if (b) return 'Through ' + b + (year ? ', ' + year : '');
    if (a) return 'From ' + a + (year ? ', ' + year : '');
    return '';
  }

  // Normalize a v2 feed into the internal shape the renderer reads. The feed is
  // v2-ONLY (spec §1.3) — legacy/unknown keys are IGNORED. Optional fields are
  // defaulted gently so partial promos never throw.
  function normalizeFeed(json) {
    if (!json) return [];
    var list = json.promotions || (Array.isArray(json) ? json : []);
    if (!Array.isArray(list)) return [];
    return list.map(function (raw, i) {
      var promo = raw || {};
      var d = (promo.dates && typeof promo.dates === 'object') ? promo.dates : {};
      var dates = {
        start: d.start || null,
        end: d.end || null,
        whileSuppliesLast: !!d.whileSuppliesLast,
        display: d.display || rangeLabel(d.start, d.end)
      };
      var st = (promo.status && typeof promo.status === 'object') ? promo.status : {};
      return {
        id: promo.id || ('promo-' + i),
        source: resolveSource(promo),
        brand: promo.brand || '',
        title: stripDollars(promo.title || ''),
        subtitle: promo.subtitle ? stripDollars(promo.subtitle) : null,
        badge: promo.badge || null,
        discountLabel: promo.discountLabel || null,
        dates: dates,
        tags: Array.isArray(promo.tags) ? promo.tags : [],
        image: promo.image || null,
        description: promo.description ? stripDollars(promo.description) : null,
        terms: promo.terms || null,
        included: Array.isArray(promo.included) ? promo.included.filter(function (r) { return r && r.label; }) : [],
        featured: !!promo.featured,
        // HC - promo-level regional gating: compiler-derived region keys pass
        // through untouched; consumers hand them to evaluatePromotionAccess. - 2026-08-01
        restrictedRegions: Array.isArray(promo.restrictedRegions) ? promo.restrictedRegions : [],
        matrixOverridable: !!promo.matrixOverridable,
        sortWeight: typeof promo.sortWeight === 'number' ? promo.sortWeight : 0,
        dateAdded: promo.dateAdded || null,
        status: {
          inStock: st.inStock === undefined ? true : !!st.inStock,
          limitedTime: st.limitedTime === undefined ? (!!dates.end || !!dates.whileSuppliesLast) : !!st.limitedTime
        }
      };
    });
  }

  function parseWindowDate(value, suffix) {
    if (!value) return null;
    var date = new Date(value + suffix);
    var ms = date.getTime();
    return isNaN(ms) ? null : ms;
  }
  function promoWindow(promo) {
    var d = promo && promo.dates;
    if (!d) return { startMs: null, endMs: null };
    return {
      startMs: parseWindowDate(d.start, 'T00:00:00'),
      endMs: d.whileSuppliesLast ? null : parseWindowDate(d.end, 'T23:59:59')
    };
  }

  // Is a promo within its active [start, end] window right now? (Same date parse
  // as urgencyChipHtml: local-time, start at 00:00:00, end at 23:59:59.) Fail
  // open on missing/invalid dates — never hide a promo on a bad or absent date.
  function isPromoLive(promo, nowMs) {
    var activeWindow = promoWindow(promo);
    if (activeWindow.startMs !== null && activeWindow.startMs > nowMs) return false; // not launched yet
    if (activeWindow.endMs !== null && activeWindow.endMs < nowMs) return false; // ended
    return true;
  }

  return {
    stripDollars: stripDollars,
    resolveSource: resolveSource,
    sourceHref: sourceHref,
    ymdLabel: ymdLabel,
    rangeLabel: rangeLabel,
    normalizeFeed: normalizeFeed,
    promoWindow: promoWindow,
    isPromoLive: isPromoLive
  };
})();
